<template>
  <div class="flex h-13">
    <div class="flex pl-6 bg-slate-100 sticky left-88 z-10">
      <div
        class="flex items-center border-b border-l border-r border-slate-400 bg-pure-white"
        :class="{ 'rounded-bl': isLast }"
      >
        <div class="px-4 flex flex-shrink-0 gap-2 w-20">
          <BlTextBody2 :text="item.id" class="truncate text-slate-1000" />
        </div>
        <div
          class="px-4 flex flex-shrink-0 gap-2 w-60 cursor-pointer"
          :title="item.name"
          @click="panelOpened = true"
        >
          <BlTextBody2
            :text="item.name"
            class="truncate underline"
            :class="
              activeProspectStage ? 'text-light-purple' : 'text-slate-1000'
            "
          />
        </div>
      </div>
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-40 flex items-center border-b border-slate-400"
    >
      <BlTextBody2 v-if="!item.current_stage_name" text="-" />
      <div v-else class="truncate" :title="item.current_stage_name">
        <BlChip :chip="item.current_stage_name" class="truncate" />
      </div>
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-50 flex items-center gap-2 border-b border-slate-400"
    >
      <BlTextBody2 v-if="!item.assigned_user" text="-" />
      <div
        v-else
        class="px-1.5 flex items-center truncate bg-slate-300 rounded"
      >
        <BlTextBody2
          :title="item.assigned_user.name || 'Deleted user'"
          :text="item.assigned_user.name || 'Deleted user'"
          class="truncate"
          :class="{ 'italic ': !item.assigned_user.name }"
        />
      </div>
    </div>
    <div
      v-if="enableBranch"
      class="px-4 flex-shrink-0 bg-pure-white w-50 flex items-center border-b border-slate-400"
    >
      <BlTextBody2 v-if="!item.branch" text="-" />
      <div
        v-else
        class="h-7 max-w-44 px-2 flex items-center justify-center rounded"
        :style="{ backgroundColor: item.branch.background_color || 'unset' }"
        :title="item.branch.name"
      >
        <BlTextBody2
          class="truncate"
          :text="item.branch.name || '-'"
          :style="{ color: item.branch.color || 'unset' }"
        />
      </div>
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-50 flex items-center gap-2 border-b border-slate-400"
    >
      <BlTextBody2 v-if="item.prospect_tags.length === 0" text="-" />
      <div
        v-else
        v-for="tag in item.prospect_tags.slice(0, 2)"
        :key="tag.id"
        class="h-7 min-w-12 px-2 flex items-center justify-center bg-slate-300 rounded truncate"
        :title="tag.name || '-'"
      >
        <BlTextBody2 :text="tag.name || '-'" class="truncate" />
      </div>

      <div class="relative" v-if="item.prospect_tags.slice(2).length">
        <div
          class="flex items-center justify-center cursor-pointer"
          @click="openMoreTags = !openMoreTags"
        >
          <BlBubbleCount
            :count="`+ ${item.prospect_tags.slice(2).length}`"
            class="h-7 hover:bg-light-purple-100"
          />
        </div>
        <div
          v-if="openMoreTags"
          class="absolute z-50 mt-10 top-0 right-0 bg-pure-white rounded border border-grey-200 p-1 gap-1 flex flex-col min-w-40 truncate"
        >
          <div
            v-for="(tag, idx) in item.prospect_tags.slice(2)"
            :key="idx"
            class="flex max-w-64"
          >
            <div
              class="px-4 py-2 text-left outline-none rounded truncate"
              :title="tag.name || '-'"
            >
              <BlTextBody2 class="truncate" :text="tag.name || '-'" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-for="stage in item.prospect_stages"
      :key="stage.id"
      class="px-4 flex-shrink-0 bg-pure-white w-50 flex items-center border-b border-slate-400"
      :title="displayDate(stage.moved_at)"
    >
      <BlTextBody2 :text="displayDate(stage.moved_at)" />
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-80 flex-grow flex items-center border-b border-slate-400"
      :title="reasonForDiscontinuedText"
    >
      <BlTextBody2
        :text="reasonForDiscontinuedText"
        class="truncate whitespace-pre-line"
      />
    </div>
    <div class="flex pr-6 bg-slate-100 sticky right-0" :style="{ zIndex: 1 }">
      <div
        class="flex-shrink-0 bg-pure-white w-23 flex items-center justify-center border-b border-r border-slate-400"
        :class="{ 'rounded-br': isLast }"
      >
        <BlInputButton
          type="outlined-error"
          dense
          label="Delete"
          @clicked="deleteOpened = true"
        />
      </div>
    </div>
    <ProspectPanel
      v-if="item.current_prospect_stage_id"
      location="table"
      :open="panelOpened"
      :id="item.current_prospect_stage_id"
      @close-panel="panelOpened = false"
      @after-commit="$emit('after-commit')"
    />
    <BlBackdrop
      v-if="deleteOpened"
      title="Delete prospect"
      label="Delete prospect"
      type="error"
      :disabled="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    >
      <div class="w-160 px-8 flex flex-col gap-4">
        <div class="flex">
          <div class="h-7 px-1 flex items-center gap-1 rounded bg-tulip-100">
            <BlIcon icon="user-square" color="tulip-7" class="h-6 w-6" />
            <BlTextSubtitle3 :text="item.name" class="text-tulip-700" />
          </div>
        </div>
        <div>
          <BlTextBody2 text="You're about to delete " />
          <BlTextBody2 :text="`${item.name} `" class="font-bold" />
          <BlTextBody2
            text="from the pipeline. All data gathered will be permanently lost. Are you sure you want to proceed? This action cannot be undone."
          />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import ProspectPanel from "../modal/ProspectPanel.vue";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlIcon from "../../BlIcon.vue";
import BlBubbleCount from "../../BlBubbleCount.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "ProspectItem",
  components: {
    ProspectPanel,
    BlTextSubtitle3,
    BlTextBody2,
    BlChip,
    BlIcon,
    BlBubbleCount,
    BlInputButton,
    BlBackdrop
  },
  props: {
    item: { type: Object, required: true },
    isLast: { default: false }
    // @after-commit
  },
  data() {
    return {
      panelOpened: false,
      openMoreTags: false,
      deleteOpened: false,
      deleteLoading: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "prospectStage"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    activeProspectStage() {
      if (this.panelOpened) return true;
      if (this.prospectStage && this.prospectStage.id === this.item.id) {
        return true;
      }
      return false;
    },
    reasonForDiscontinuedText() {
      if (!this.item.prospect_stages || !this.item.prospect_stages.length) {
        return "-";
      }
      const found = this.item.prospect_stages.find(i =>
        i.stage_name.toLowerCase().includes("discontinued")
      );
      if (!found) return "-";
      if (found.discontinuation_reason === "Other") {
        return found.other_discontinuation_reason || "Other";
      }
      return found.discontinuation_reason || "-";
    }
  },
  watch: {
    openMoreTags(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMore);
        }, 10);
      }
    }
  },
  methods: {
    ...mapActions(["deleteProspect"]),
    displayDate(val) {
      if (!val) return "-";
      const date = val.split(" ")[0];
      const time = val.split(" ")[1];
      return `${moment(date, "DD/MM/YYYY").format("DD MMMM YYYY")} ${time}`;
    },
    closeHandlerMore() {
      window.removeEventListener("click", this.closeHandlerMore);
      this.openMoreTags = false;
    },
    onRemove() {
      const data = {
        id: this.item.id,
        prospect_name: this.item.name
      };
      this.deleteLoading = true;
      this.deleteProspect(data).then(({ success }) => {
        this.deleteLoading = true;
        if (!success) return;
        this.$emit("after-commit");
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/prospects/Item.vue